// src/components/Modals/ChildrenModals.js
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { X, User } from "lucide-react";
import Button from "../UI/Button";

// ✅ re-export safely (works even if those files use default export)
export { default as EditProfileModal } from "./EditProfileModal";
export { default as ChildDetailModal } from "./ChildDetailModal";
export { default as ReintegrationModal } from "./ReintegrationModal";
export { default as ReintegrationDetailsModal } from "./ReintegrationDetailsModal";

/* ===========================
   Avatar Helpers (NO AI photos)
   =========================== */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
  return (first + last).toUpperCase();
};

const isValidPhoto = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return false;
  if (s.includes("pravatar.cc")) return false; // block old fallback
  return true;
};

/**
 * ✅ FIXED ChildAvatar:
 * - Works with Cloudinary https://... and local blob preview URL
 * - Proper fallback (initials/icon) when image fails
 * - Doesn't rely on hiding <img> (which leaves empty space)
 */
export const ChildAvatar = ({ fullName, photoUrl, size = 48, className = "" }) => {
  const initials = getInitials(fullName);
  const [broken, setBroken] = useState(false);

  // reset broken on URL change
  useEffect(() => setBroken(false), [photoUrl]);

  const canShow = isValidPhoto(photoUrl) && !broken;

  if (canShow) {
    return (
      <img
        src={photoUrl}
        alt={fullName}
        className={[
          "rounded-full object-cover border border-gray-200 dark:border-gray-800 shrink-0",
          className,
        ].join(" ")}
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    );
  }

  return (
    <div
      className={[
        "rounded-full flex items-center justify-center shrink-0",
        "border border-gray-200 dark:border-gray-800",
        "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold",
        className,
      ].join(" ")}
      style={{ width: size, height: size }}
      title={fullName}
    >
      {initials || <User size={18} />}
    </div>
  );
};

/* ----------------- Badge Color Helpers ----------------- */
export const getStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/30 dark:text-green-200 dark:border-green-900";
    case "Transitioning":
      return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-200 dark:border-yellow-900";
    case "Transferred":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900";
    case "Reintegrated":
      return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/30 dark:text-purple-200 dark:border-purple-900";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700";
  }
};

export const getHealthStatusColor = (status) => {
  switch (status) {
    case "Excellent":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/30 dark:text-green-200 dark:border-green-900";
    case "Good":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900";
    case "Needs Check-up":
      return "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/30 dark:text-orange-200 dark:border-orange-900";
    case "Requires Attention":
      return "bg-red-100 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700";
  }
};

export const getAdoptionStatusColor = (status) => {
  switch (status) {
    case "Open for Adoption":
      return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900";
    case "Adopted":
      return "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/30 dark:text-green-200 dark:border-green-900";
    case "Not Available for Adoption":
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700";
  }
};

/* ----------------- Dashboard-like Modal Shell ----------------- */
const ModalShell = ({ title, subtitle, onClose, children, maxWidth = "max-w-3xl" }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 flex items-center justify-center p-3 md:p-4">
      <div
        className={`w-full ${maxWidth} max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden flex flex-col transition-colors duration-300`}
      >
        {/* Header */}
        <div className="px-5 md:px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p> : null}
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300 transition-colors"
            type="button"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800" />

        {/* Body */}
        <div className="px-5 md:px-6 py-6 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

/* ----------------- Small UI Helpers ----------------- */
const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 " +
      (props.className || "")
    }
  />
);

const Select = (props) => (
  <select
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 " +
      (props.className || "")
    }
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={
      "w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-950 border border-transparent " +
      "focus:bg-white dark:focus:bg-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-500/30 " +
      "outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 " +
      "min-h-[110px] resize-none " +
      (props.className || "")
    }
  />
);

/* ===========================
   AddChildModal (Cloudinary-ready)
   - uses dateOfBirth instead of age
   - MUST use name="photo" so your submit code catches it if you ever read by name
   - Preview uses blob URL
   =========================== */
export const AddChildModal = ({ onClose, onAddChild }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    admissionDate: "",
    house: "",
    houseParent: "",
    address: "",
    healthStatus: "Good",
    educationLevel: "",
    emergencyContact: "",
    caseType: "",
    status: "Active",
    adoptionStatus: "Not Available for Adoption",
    notes: "",
    photo: null,
    lastCheckup: "",
  });

  const [preview, setPreview] = useState(null);

  const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5MB
  const allowedPhotoTypes = ["image/jpeg", "image/png", "image/webp"];

  const canSubmit =
    formData.firstName?.trim() &&
    formData.lastName?.trim() &&
    formData.dateOfBirth &&
    formData.gender?.trim() &&
    formData.house?.trim() &&
    formData.houseParent?.trim() &&
    formData.educationLevel?.trim() &&
    formData.emergencyContact?.trim() &&
    formData.caseType?.trim() &&
    formData.address?.trim();

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleChange = (e) => {
    setFormData((p) => ({
      ...p,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      setFormData((p) => ({ ...p, photo: null }));
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      return;
    }

    // ✅ type validation
    if (!file.type?.startsWith("image/") || (allowedPhotoTypes.length && !allowedPhotoTypes.includes(file.type))) {
      toast.error("Please upload a valid image (JPG/PNG/WebP).");
      e.target.value = "";
      return;
    }

    // ✅ size validation
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo is too large. Max 5MB.");
      e.target.value = "";
      return;
    }

    setFormData((p) => ({ ...p, photo: file }));

    if (preview) URL.revokeObjectURL(preview);
    const url = URL.createObjectURL(file);
    setPreview(url);

    toast.success("Photo selected.");
  };

  const fullName = `${formData.firstName} ${formData.middleName ? formData.middleName + " " : ""}${formData.lastName}`.trim();

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.error("Please complete all required fields before submitting.");
      return;
    }

    onAddChild(formData); // parent handles loading/success/error toast
  };

  return (
    <ModalShell
      title="Add New Child"
      subtitle="Fill in the information below to add a new child to the system."
      onClose={onClose}
      maxWidth="max-w-6xl"
    >
      <div className="flex items-center gap-3 mb-5">
        <ChildAvatar fullName={fullName || "New Child"} photoUrl={preview} size={48} />
        <div className="min-w-0">
          <p className="text-sm text-gray-600 dark:text-gray-400">Creating record for</p>
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            {fullName || "New Child"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="First Name">
            <Input name="firstName" value={formData.firstName} onChange={handleChange} required />
          </Field>
          <Field label="Middle Name">
            <Input name="middleName" value={formData.middleName} onChange={handleChange} />
          </Field>
          <Field label="Last Name">
            <Input name="lastName" value={formData.lastName} onChange={handleChange} required />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Date of Birth">
            <Input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              required
            />
          </Field>
          <Field label="Gender">
            <Select name="gender" value={formData.gender} onChange={handleChange} required>
              <option value="">Select gender</option>
              <option>Male</option>
              <option>Female</option>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Admission Date">
            <Input type="date" name="admissionDate" value={formData.admissionDate} onChange={handleChange} />
          </Field>
          <Field label="Last Check-up">
            <Input type="date" name="lastCheckup" value={formData.lastCheckup} onChange={handleChange} />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Home">
            <Select
              name="house"
              value={formData.house}
              onChange={handleChange}
              required
            >
              <option value="">Select Home</option>
              <option>Home 1</option>
              <option>Home 2</option>
              <option>Home 3</option>
              <option>Home 4</option>
            </Select>
          </Field>

          <Field label="House Parent">
            <Select
              name="houseParent"
              value={formData.houseParent}
              onChange={handleChange}
              required
            >
              <option value="">Select House Parent</option>
              <option>Romeo & Lucia Torremocha</option>
              <option>Arnel & Grace Bermoy</option>
              <option>Greg & Geraldine Torremocha</option>
              <option>Mariel & Alvin Torremocha</option>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Health Status">
            <Select name="healthStatus" value={formData.healthStatus} onChange={handleChange}>
              <option>Excellent</option>
              <option>Good</option>
              <option>Needs Check-up</option>
              <option>Requires Attention</option>
            </Select>
          </Field>

          <Field label="Education Level">
            <Input
              name="educationLevel"
              value={formData.educationLevel}
              onChange={handleChange}
              placeholder="e.g. Grade 1, Pre-school"
              required
            />
          </Field>
        </div>

        <Field label="Emergency Contact">
          <Input
            type="tel"
            name="emergencyContact"
            value={formData.emergencyContact}
            onChange={(e) =>
              handleChange({
                target: {
                  name: "emergencyContact",
                  value: e.target.value.replace(/[^\d+]/g, ""),
                },
              })
            }
            placeholder="Enter contact number"
            required
          />
        </Field>

        <Field label="Address">
          <Textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Enter child's address or place of origin"
            required
          />
        </Field>

        <Field label="Case Type">
          <Select name="caseType" value={formData.caseType} onChange={handleChange} required>
            <option value="">Select case type</option>
            <option>Orphan</option>
            <option>Abandoned</option>
            <option>Surrender</option>
            <option>Neglected</option>
            <option>Abused</option>
            <option>Child Dependent</option>
            <option>Child at Risk</option>
            <option>VAWC</option>
          </Select>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Status">
            <Select name="status" value={formData.status} onChange={handleChange}>
              <option>Active</option>
              <option>Inactive</option>
              <option>Transitioning</option>
              <option>Transferred</option>
              <option>Reintegrated</option>
            </Select>
          </Field>

          <Field label="Adoption Status">
            <Select name="adoptionStatus" value={formData.adoptionStatus} onChange={handleChange}>
              <option>Not Available for Adoption</option>
              <option>Open for Adoption</option>
              <option>Adopted</option>
            </Select>
          </Field>
        </div>

        <Field label="Profile Image / Photo">
          <Input name="photo" type="file" accept="image/*" onChange={handleFileChange} />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Leave empty if you don’t want to add a photo.
          </p>
        </Field>

        <Field label="Notes">
          <Textarea name="notes" value={formData.notes} onChange={handleChange} />
        </Field>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" size="medium" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            disabled={!canSubmit}
            className="flex items-center gap-2"
          >
            Add Child
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};